import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../../hooks/http.hook';
import { rewardsFromDiscountMap } from '../../../utils/productGroups';
import CreateBusinessHeader from '../../business/components/CreateBusinessHeader';
import CreateFooter from '../../business/components/CreateFooter';
import PromotionNameImageForm from '../components/PromotionNameImageForm';
import DeletePromotionButton from '../components/DeletePromotionButton';
import PromotionProductsPicker, {
  PromotionPickerOpenButton,
} from '../components/PromotionProductsPicker';
import { createDiscountPromotion } from '../handlers/discountPromotion';
import '../../business/Business.css';
import './CreateDiscountPromotionPage.css';

function CreateDiscountPromotionPage() {
  const navigate = useNavigate();
  const { promotionId } = useParams();
  const { requestWithMeta } = useHttp();
  const isEdit = !!promotionId;

  const [name, setName] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');
  const [isImageUploading, setIsImageUploading] = React.useState(false);
  const [promotionLoading, setPromotionLoading] = React.useState(false);
  const [discounts, setDiscounts] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [openPicker, setOpenPicker] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!isEdit) return;

    setPromotionLoading(true);
    setSubmitError(null);

    (async () => {
      try {
        const res = await requestWithMeta(`/operator/promotions/${encodeURIComponent(promotionId)}`, 'GET');
        if (cancelled) return;
        if (!res?.ok) {
          setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить акцию');
          return;
        }

        const promo = res?.data ?? {};
        setName(promo?.name ?? '');
        setImageUrl(promo?.imageUrl ?? promo?.image_url ?? '');

        const rewards = Array.isArray(promo?.rewards) ? promo.rewards : [];
        const nextDiscounts = {};
        rewards.forEach((r) => {
          const pid = r?.productId ?? r?.product_id;
          const d = r?.discount;
          if (pid == null) return;
          if (d === null || d === undefined) return;
          const n = Number(d);
          if (!Number.isFinite(n)) return;
          nextDiscounts[pid] = n;
        });
        setDiscounts(nextDiscounts);
      } catch (e) {
        if (!cancelled) {
          setSubmitError(e?.message || 'Не удалось загрузить акцию');
        }
      } finally {
        if (!cancelled) setPromotionLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isEdit, promotionId, requestWithMeta]);

  const handleDiscountChange = (productId, value) => {
    const num = value === '' ? '' : Math.min(100, Math.max(0, Number(value) || 0));
    setDiscounts((prev) => ({ ...prev, [productId]: num }));
  };

  const rewardsWithDiscount = React.useMemo(
    () => rewardsFromDiscountMap(discounts),
    [discounts]
  );

  const canSubmit =
    name.trim().length > 0 &&
    rewardsWithDiscount.length > 0 &&
    !submitting &&
    !isImageUploading &&
    !promotionLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = isEdit
        ? await requestWithMeta(`/operator/promotions/${encodeURIComponent(promotionId)}`, 'PUT', {
            name: name.trim(),
            imageUrl: imageUrl.trim() || null,
            conditions: [],
            rewards: rewardsWithDiscount
          })
        : await createDiscountPromotion(requestWithMeta, {
            name: name.trim(),
            imageUrl: imageUrl.trim() || null,
            rewards: rewardsWithDiscount
          });

      if (res.ok) {
        navigate('/promotions');
        return;
      }
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось создать акцию');
    } catch (e) {
      setSubmitError(e?.message || 'Ошибка при создании акции');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (openPicker) {
    return (
      <PromotionProductsPicker
        title="Скидки по товарам"
        subtitle="Укажите скидку по каждому товару"
        mode="discount"
        discounts={discounts}
        onDiscountChange={handleDiscountChange}
        onClose={() => setOpenPicker(false)}
      />
    );
  }

  return (
    <div className="create-discount-promo-root">
      <CreateBusinessHeader
        title={'Скидка'}
        subtitle="Укажите скидку по каждому товару"
        onBack={handleBack}
      />

      <main className="create-discount-promo-content">
        {promotionLoading ? (
          <div className="create-discount-promo-loading" aria-busy="true">
            Загрузка акции…
          </div>
        ) : null}

        <PromotionNameImageForm
          name={name}
          onNameChange={setName}
          imageUrl={imageUrl}
          onImageUrlChange={setImageUrl}
          onImageUploadLoadingChange={setIsImageUploading}
        />

        <section className="shop-create-section">
          <PromotionPickerOpenButton
            title="Скидки по товарам"
            hint={
              rewardsWithDiscount.length > 0
                ? `Выбрано: ${rewardsWithDiscount.length}`
                : 'Указать скидки'
            }
            onClick={() => setOpenPicker(true)}
          />
        </section>

        {submitError ? (
          <div className="create-discount-promo-error">{submitError}</div>
        ) : null}
      </main>

      <CreateFooter
        disabled={!canSubmit}
        onCreate={handleSubmit}
        label={submitting ? (isEdit ? 'Сохранение…' : 'Создание…') : (isEdit ? 'Сохранить' : 'Создать акцию')}
        leading={
          isEdit ? (
            <DeletePromotionButton
              promotionId={promotionId}
              promotionName={name}
              requestWithMeta={requestWithMeta}
              disabled={submitting}
              onDeleted={() => navigate('/promotions')}
            />
          ) : null
        }
      />
    </div>
  );
}

export default CreateDiscountPromotionPage;
